import { CmsPageQuery } from '.gql/graphql';
import PageBuilder from '@/components/pagebuilder/pageBuilder';
import Divider from '@/components/ui/divider';
import { withStaticProps } from '@/server/app';
import { apolloClient, gql } from '@/utils/apollo';
import { decodeURIPart, getCategoryRoute, getProductRoute } from '@/utils/routes';
import { GetStaticPaths } from 'next';
import { DefaultSeo } from 'next-seo';

interface DynamicPageProps {
  cmsPage: CmsPageQuery['cmsPage'];
}

export default function DynamicPage(props: DynamicPageProps) {
  const { cmsPage } = props;

  return (
    <>
      <DefaultSeo
        title={cmsPage?.meta_title || cmsPage?.title || undefined}
        description={cmsPage?.meta_description || undefined}
      />

      <Divider className="mb-0 pt-0" />
      <PageBuilder pagebuilder={cmsPage?.pagebuilder} />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = withStaticProps(async ({ params, store }) => {
  const url = decodeURIPart((params?.args?.[0] as string) ?? '??');

  const productUrlKey = store.product_url_suffix ? url.replace(store.product_url_suffix, '') : url;
  const categoryUrlKey = store.category_url_suffix ? url.replace(store.category_url_suffix, '') : url;

  if (productUrlKey) {
    const product = (
      await apolloClient.query({
        query: gql(`
          query ProductPage($urlKey: String!) {
            products(filter: { url_key: { eq: $urlKey } }) {
              items {
                id: uid
                uid
              }
            }
          }
        `),
        variables: {
          urlKey: productUrlKey,
        },
      })
    ).data.products?.items?.[0];

    if (product) {
      return {
        redirect: {
          permanent: false,
          destination: getProductRoute(productUrlKey),
        },
      };
    }
  }

  if (categoryUrlKey) {
    const category = (
      await apolloClient.query({
        query: gql(`
          query CategoryPage($urlKey: String!) {
            categories(filters: { url_key: { eq: $urlKey } }) {
              items {
                id: uid
                uid
              }
            }
          }
        `),
        variables: {
          urlKey: categoryUrlKey,
        },
      })
    ).data.categories?.items?.[0];

    if (category) {
      return {
        redirect: {
          permanent: false,
          destination: getCategoryRoute(categoryUrlKey),
        },
      };
    }
  }

  const cmsPage = (
    await apolloClient.query({
      query: gql(`
        query CmsPage($urlKey: String!) {
          cmsPage(identifier: $urlKey) {
            title
            meta_title
            meta_description
            identifier
            content_heading
            pagebuilder {
              html
              components
            }
          }
        }
      `),
      variables: {
        urlKey: url,
      },
    })
  ).data.cmsPage;

  if (cmsPage) {
    return {
      props: {
        cmsPage,
      },
    };
  }

  return {
    notFound: true,
  };
});
